<template>
  <ion-page>
    <!-- Account Dropdown -->
    <ion-fab
      v-if="currentUser && userDoc && isOnboarded && isInstructor" id="account-fab-button" slot="fixed"
      ref="accountFabButton" vertical="bottom" :activated="accountFabButtonOpen"
      horizontal="end" edge="false" style="bottom:0; border:none;"
    >
      <ion-fab-button color="light">
        <ion-label style="margin-bottom: 6px">
          <ion-icon aria-hidden="true" :icon="settingsOutline" style="height: 22px; width: 22px;" />
          <br>
          Account
        </ion-label>
      </ion-fab-button>
      <ion-fab-list side="top">
        <ion-button :router-link="{name: 'account'}" :color="route.name == 'account' ? 'primary' : 'medium'" fill="outline">
          <ion-label>Profile</ion-label>
        </ion-button>
        <ion-button :router-link="{ name: 'bookingPrioritySettings' }" :color="route.name == 'bookingPrioritySettings' ? 'primary' : 'medium'" fill="outline">
          <ion-label>Booking Priority</ion-label>
        </ion-button>
        <ion-button
          v-if="isInstructor" :router-link="{name: 'reports'}" :color="route.name == 'reports' ? 'primary' : 'medium'"
          fill="outline"
        >
          <ion-label>Reports</ion-label>
        </ion-button>
      </ion-fab-list>
    </ion-fab>
    <ion-tabs v-if="currentUser && userDoc">
      <sm-header />
      <ion-page v-if="currentUser && !userDoc?.onboarded && route.name != 'account'" class="ion-padding">
        <ion-content color="light" class="ion-padding">
          <p style="margin-top: 3em">Please setup your account to start your journey.</p>
          <ion-button expand="block" color="success" :router-link="{name: 'account'}">Setup Account</ion-button>
        </ion-content>
      </ion-page>
      <ion-router-outlet />
      <ion-tab-bar slot="bottom" data-testid="bottom-tabs-menu">
        <template v-if="isOnboarded">
          <ion-tab-button tab="home" href="/my/home">
            <template v-if="userStore.is.demoStudent">
              <ion-badge id="demo-student-badge" color="tertiary" class="animate__animated animate__pulse animate__infinite animate__slow">DEMO</ion-badge>
            </template>
            <ion-icon aria-hidden="true" :icon="homeOutline" />
            <ion-label>Home</ion-label>
          </ion-tab-button>

          <ion-tab-button v-if="isInstructor" tab="schedules" href="/my/schedules">
            <ion-icon
              aria-hidden="true" :icon="calendarOutline"
              :class="!hasSchedules ? 'animate__animated animate__swing animate__infinite' : ''"
            />
            <ion-label>Schedule</ion-label>
          </ion-tab-button>

          <ion-tab-button
            tab="lessons" class="tab-color-override"
            :selected="route.name == 'lessons'"
            @click="changeRouteWithoutAnimation('/my/lessons/')"
          >
            <ion-icon aria-hidden="true" :icon="golfOutline" />
            <ion-label>
              Lessons
              <pending-lessons-count />
            </ion-label>
          </ion-tab-button>

          <ion-tab-button
            v-if="isStudent" href="/my/lessons/new" tab="newLesson"
            :selected="route.name == 'newLesson'"
            @click="changeRouteWithoutAnimation('/my/lessons/new')"
          >
            <ion-icon
              aria-hidden="true"
              :icon="addCircleOutline"
            />
            <ion-label>Book Lesson</ion-label>
          </ion-tab-button>

          <ion-tab-button v-if="isInstructor" tab="students" href="/my/students">
            <ion-icon
              aria-hidden="true" :icon="peopleOutline"
            />
            <ion-label>Students</ion-label>
          </ion-tab-button>
        </template>
        <ion-tab-button
          v-if="isStudent" tab="account" href="/my/account"
        >
          <ion-icon aria-hidden="true" :icon="settingsOutline" />
          <ion-label>Account</ion-label>
        </ion-tab-button>
        <ion-tab-button
          v-if="isInstructor" tab="account" href="#"
          :selected="route.name == 'account' || route.name == 'reports'"
          @click="openAccountFabButton"
        >
          <ion-icon aria-hidden="true" :icon="settingsOutline" />
          <ion-label>Account</ion-label>
        </ion-tab-button>
      </ion-tab-bar>
    </ion-tabs>
  </ion-page>
</template>

<script setup>
import {
  IonBadge, IonButton, IonContent, IonFab, IonFabButton, IonFabList, IonIcon, IonLabel, IonPage, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, useIonRouter,
} from '@ionic/vue'
import { addCircleOutline, calendarOutline, golfOutline, homeOutline, peopleOutline, settingsOutline } from 'ionicons/icons'
import { computed, ref, watch } from 'vue'
import { useCurrentUser } from 'vuefire'
import { useDocument } from 'vuefire'
import { useRoute } from 'vue-router'

import { BLACKHAWK_ACADEMY_DEFAULT_LOCATION, branding, identifyUser } from '@/globals'
import PendingLessonsCount from '@/4_features/menu/PendingLessonsCount.vue'
import SmHeader from '@/views/layout/SmHeader.vue'
import { useUserStore } from '@/stores/user'

const route = useRoute()
const ionRouter = useIonRouter()
const currentUser = useCurrentUser()
const userStore = useUserStore()
const userDoc = useDocument(userStore.userDocRef)

const isOnboarded = computed(() => userDoc?.value?.onboarded)
const isInstructor = computed(() => userDoc?.value?.type === 'instructor')
const isStudent = computed(() => userDoc?.value?.type === 'student')
const hasSchedules = computed(() => userDoc?.value?.instructor?.lessonTypes?.length > 0)

watch(userDoc, async () => {
  if (!userDoc.value) return
  identifyUser(userDoc.value)
  if (!branding.defaultLocations) { // Default swingmatch website
    if (userDoc.value?.homeClub?.uuid === BLACKHAWK_ACADEMY_DEFAULT_LOCATION) {
      console.log('Default swingmatch website but home club is blackhawk academy. Redirecting to the blackhawk academy website.')

      window.location.hostname = window.location.hostname
        .replace('app.', 'blackhawkacademy.')
        .replace('ios.test', 'blackhawkacademy.ios.test')
        .replace('android.test', 'blackhawkacademy.android.test')
    }
  }
})

async function changeRouteWithoutAnimation(to) {
  console.log('ignore following error (internal ion-router bug)')
  ionRouter.push(to, 'none')
}

const accountFabButtonOpen = ref(false)
async function openAccountFabButton() {
  accountFabButtonOpen.value = !accountFabButtonOpen.value
}

const accountFabButton = ref()
watch(() => route.name, () => {
  accountFabButton.value?.$el?.close()
  accountFabButtonOpen.value = false
})
</script>

<style scoped>
#tab-button-newLesson {
  color: green;
  /* transform:scale(1.2); */
}
#tab-button-newLesson ion-label {
  font-weight: bold;
  margin-top: -2px;
}
#tab-button-newLesson.tab-selected{
  color: var(--ion-color-primary) !important;
}
#demo-student-badge {
  position: absolute;
  top: 0;
  left: 10px;
}
:deep(#account-fab-button ion-fab-button::part(native)) {
  opacity: 0;
}
:deep(#account-fab-button ion-fab-list) {
  right: 0;
  align-items: flex-end;
}
:deep(#account-fab-button ion-button) {
  background: var(--ion-color-light);
  border-radius: 15px;
  min-width: 155px;
}
</style>
